import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuesService } from '@services/menues/menues.service';
import { SignoSeguridadService } from '@services/seguridad/signo-seguridad.service';
import { StorageService } from '@services/shared/storage.service';
import { AuthService } from '@services/auth/auth.service';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';
import { MenuTreeNode } from '@interfaces/interface-bakend/seguridad/acl/menu.interface';

interface TreeNode {
    _id: string;
    name: string;
    rawName: string;
    redirectTo: string;
    level: number;
    tipo: string;
    order: number;
    icon: string;
    expanded: boolean;
    visible: boolean;
    children: TreeNode[];
    parentId: string | null;
}

@Component({
    selector: 'app-menues-administracion',
    templateUrl: './menues-administracion.component.html',
    styleUrls: ['./menues-administracion.component.scss'],
})
export class MenuesAdministracionComponent implements OnInit, OnDestroy {

    treeRoots: TreeNode[] = [];
    flagLoaded: boolean = false;
    termino: string = '';
    allExpanded: boolean = false;

    menuToDelete: string | null = null;

    constructor(
        private router: Router,
        private menuesService: MenuesService,
        private signoSeguridadService: SignoSeguridadService,
        private storageService: StorageService,
        private authService: AuthService,
        private modalCustom: PopUpModalCustomService,
    ) {}

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    async ngOnInit(): Promise<void> {
        if (!(await this.signoSeguridadService.puedeAccederMenues())) {
            this.signoSeguridadService.setAccesoDenegado(true);
            this.router.navigateByUrl('/configurar-administracion');
            return;
        }
        await this.cargarMenues();
    }

    private async cargarMenues(): Promise<void> {
        this.treeRoots = await this.buildTree();
        this.flagLoaded = true;
    }

    /**
     * Arma el arbol con una sola llamada: el backend devuelve el bosque completo
     * ya anidado y con los nombres traducidos.
     */
    private async buildTree(): Promise<TreeNode[]> {
        const tree = await this.menuesService.getMenuTree(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        return (tree || []).map((node) => this.toTreeNode(node));
    }

    private toTreeNode(node: MenuTreeNode): TreeNode {
        const mapped: TreeNode = {
            _id: node._id,
            name: node.name || '',
            rawName: node.rawName || node.name || '',
            redirectTo: node.redirectTo || '',
            level: node.level || 1,
            tipo: node.tipo || 'WEB',
            order: node.order || 0,
            icon: node.icon || '',
            expanded: false,
            visible: true,
            children: [],
            parentId: node.parent || null,
        };

        mapped.children = (node.children || []).map((child) => this.toTreeNode(child));
        return mapped;
    }

    aplicarFiltro(termino: string): void {
        this.termino = termino;
        const q = (termino || '').trim().toLowerCase();

        if (!q) {
            const resetVisible = (nodes: TreeNode[]) => {
                for (const n of nodes) {
                    n.visible = true;
                    resetVisible(n.children);
                }
            };
            resetVisible(this.treeRoots);
            return;
        }

        const markVisible = (nodes: TreeNode[]): boolean => {
            let anyChild = false;
            for (const n of nodes) {
                const selfMatch = n.name.toLowerCase().includes(q) ||
                    n.redirectTo.toLowerCase().includes(q);
                const childMatch = markVisible(n.children);
                n.visible = selfMatch || childMatch;
                if (n.visible) { anyChild = true; }
            }
            return anyChild;
        };
        markVisible(this.treeRoots);
    }

    toggleExpand(node: TreeNode): void {
        node.expanded = !node.expanded;
    }

    toggleExpandAll(): void {
        if (this.allExpanded) {
            this.collapseAll();
        } else {
            this.expandAll();
        }
    }

    private expandAll(): void {
        const expand = (nodes: TreeNode[]) => {
            for (const n of nodes) {
                n.expanded = true;
                expand(n.children);
            }
        };
        expand(this.treeRoots);
        this.allExpanded = true;
    }

    private collapseAll(): void {
        const collapse = (nodes: TreeNode[]) => {
            for (const n of nodes) {
                n.expanded = false;
                collapse(n.children);
            }
        };
        collapse(this.treeRoots);
        this.allExpanded = false;
    }

    crearNuevo(): void {
        this.router.navigateByUrl('/menu-edicion');
    }

    editarMenu(menu: TreeNode): void {
        this.router.navigateByUrl(`/menu-edicion?id=${menu._id}`);
    }

    confirmarEliminar(menu: TreeNode): void {
        this.menuToDelete = menu._id;
        this.modalCustom.abrirPopup({
            titulo: 'Eliminar menú',
            mensaje: `¿Está seguro de eliminar "${menu.name}"? Esta acción puede afectar la navegación de usuarios con este permiso.`,
            tipo: 'warning',
            textoBoton: 'Eliminar',
            textoBotonCancelar: 'Cancelar',
            accionConfirmar: () => this.eliminarMenu(),
            accionCerrar: () => { this.menuToDelete = null; },
        });
    }

    async eliminarMenu(): Promise<void> {
        if (!this.menuToDelete) { return; }
        try {
            await this.menuesService.deleteMenu(this.menuToDelete);
            const user = await this.authService.getUserLogon();
            this.storageService.clearMenuCache('es', user?.perfil?._id || '');

            this.modalCustom.abrirPopup({
                titulo: 'Menú eliminado',
                mensaje: 'El menú se eliminó correctamente.',
                tipo: 'success',
            });

            await this.cargarMenues();
        } catch {
            this.modalCustom.abrirPopup({
                titulo: 'Error al eliminar',
                mensaje: 'Ocurrió un error al eliminar el menú. Intente nuevamente.',
                tipo: 'error',
            });
        }
        this.menuToDelete = null;
    }

    async cambiarOrden(node: TreeNode, delta: number): Promise<void> {
        const newOrder = (node.order || 1) + delta;
        if (newOrder < 1) { return; }
        await this.menuesService.updateMenu(node._id, {
            _id: node._id,
            order: newOrder,
            name: node.rawName,
            redirectTo: node.redirectTo,
            icon: node.icon || 'string',
            path: 'string',
            item: 'string',
        });
        node.order = newOrder;
        this.ordenarArbol(this.treeRoots);
    }

    private ordenarArbol(nodes: TreeNode[]): void {
        nodes.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        for (const n of nodes) {
            this.ordenarArbol(n.children);
        }
    }

    volver(): void {
        this.router.navigateByUrl('/menu-seguridad');
    }
}
